<template>
   <div class="pb-1">
      <div class="portal-input rounded-md" data-cy="short-text-v1">
         <label :for="block.id" class="sr-only">{{ block.heading }}</label>
         <input
            :id="block.id"
            v-model.trim="state.textInput"
            placeholder=""
            :data-cy="`short-text-${block.id}`"
            class="input block w-full rounded-lg border border-none bg-grey-10 px-3 py-4 text-black"
            type="text"
            :name="`short-text-${block.id}`"
            @input="processInputs"
         />
         <div class="focus-border"></div>
      </div>
      <!-- validation -->
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
   </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";

export interface ShortTextAnswerData {
   text: string;
}
export interface ShortTextBlock extends FlowselBlock {
   data: {
      settings: {
         validations: {
            noSpecialCharacters: boolean;
            maxLength: string;
         };
      };
   };
}
interface Props {
   block: ShortTextBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// emits
// -----------------------------------------
const emit = defineEmits(["update-valid", "update-answer"]);

// props
// -----------------------------------------
const props = defineProps<Props>();

// data
// -----------------------------------------
const state = reactive({
   textInput: props.userBlockAnswer?.data?.text || "",
});

const { t: $t } = useI18n();

// validation rules
// -----------------------------------------
/** Custom validation - no < or > characters
 * @param {string} input to be validated
 */
const noElementTags = (input) => {
   // Validation settings may be changed in admin for short text blocks so we'll always check if they are wanted
   if (!props.block.data?.settings?.validations?.noSpecialCharacters) return true;
   return !input.includes("<") && !input.includes(">");
};

const maxCharacters = props.block.data?.settings?.validations?.maxLength
   ? parseInt(props.block.data?.settings?.validations?.maxLength)
   : 0;

const rules = {
   textInput: {
      noElementTags,
      ...(props.block.isRequired && { required }),
      ...(maxCharacters && { maxLength: maxLength(maxCharacters) }),
   },
};

const $v = useVuelidate(rules, state);

// validation - computed
// -----------------------------------------
const errorList = computed(() => {
   return $v.value.textInput.$errors.map((vError) => {
      return {
         inputId: props.block.id,
         validator: vError.$validator,
         message: getErrorMessage(vError.$validator),
      };
   });
});

// validation - methods
// -----------------------------------------
/** Get the error message for the given validator
 * @param {string} validator validator type ex 'required'
 */
const getErrorMessage = (validator) => {
   if (validator === "noElementTags") return $t("validation.text.invalid_characters");
   else if (validator === "required") return $t("validation.text.required");
   else if (validator === "maxLength") return $t("validation.text.max_length");
   else return null;
};

// page - methods
// -----------------------------------------
/** On input, check if the input is valid, and emit the answer */
const processInputs = () => {
   // We'll set errors to be counted immediately, because they will only show once next is clicked
   // Once next is clicked though, they should show even if input has not been touched
   if (!$v.value.textInput.$dirty) $v.value.textInput.$touch();

   if ($v.value.textInput.$error || $v.value.$invalid) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.textInput ? { text: state.textInput } : null;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
};

// This is needed on initialise because validity is being evaluated & answers updated on input
// so if you go back or already have an answer, it would not be automatically evaluated
processInputs();
</script>

<style lang="scss" scoped></style>
